import { useEffect, useRef, useState } from 'react';
import type { IDockviewPanelHeaderProps, IDockviewHeaderActionsProps } from 'dockview-react';
import { GripVertical, X } from 'lucide-react';
import { PanelHeaderMenu } from './panel-header-menu.js';
import type { PanelComponentKey, PanelParams } from './components.js';
import { findPanelDef } from './panel-registry.js';
import { useAppStore, usePanel, type PanelId, type TabId } from './store.js';

// Titles baked into older saved layouts. The header derives its label from the
// PANEL_REGISTRY by component, so a panel renamed in the registry shows the new
// name everywhere — but only when the persisted title is one of these stale
// defaults (or empty). A title the user typed themselves is left untouched.
const STALE_PANEL_TITLES = new Set<string>([
  'Screener',
  'Company Overview',
  'Watchlist',
  'Bolt-On Screening',
  'Who’s Who (Consolidators)',
  'Market Opportunity',
  'Corryk Origination Signal',
  'Operating Pressure',
  'Reimbursement Pressure',
  'Build',
]);

/** The label to show in a panel header: the current registry name when the
 *  persisted title is empty or a known stale default, else the (user-set) title. */
function displayTitle(rawTitle: string, component: string): string {
  const def = findPanelDef(component as PanelComponentKey);
  if (def && (rawTitle === '' || STALE_PANEL_TITLES.has(rawTitle))) return def.title;
  return rawTitle;
}

// Dockview tab — text title + the unified PanelHeaderMenu (≡). Close
// and undock live inside that menu now, so the per-tab close X and
// the group-level float / close-all buttons have been deleted to
// avoid three competing affordances.
//
// Double-click the title to rename. Enter commits, Escape cancels,
// blur commits (so clicking elsewhere is the same as Enter). The
// dockview-side title is set via panel.api.setTitle so the change
// shows up in the saved layout snapshot too.

export function CustomTab(props: IDockviewPanelHeaderProps<PanelParams>) {
  // Be defensive: a stale persisted layout or an orphaned panel created
  // outside the normal addPanel path can leave params undefined or
  // partially populated. Default to the dockview-provided panel id so
  // Delete via dockview still works even when the store has lost the
  // panel; tabId may still be undefined for those — the menu degrades
  // gracefully (only Delete is offered).
  const tabId = (props.params?.tabId ?? '') as PanelParams['tabId'];
  const panelId = (props.params?.panelId ?? props.api.id) as PanelParams['panelId'];
  const panel = usePanel(tabId, panelId);
  const setPanelTitle = useAppStore((s) => s.actions.setPanelTitle);
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);

  // Self-heal a store/dockview desync. A panel restored from a persisted
  // dockview layout whose matching store record is missing (an older
  // layout, a migration that dropped the record, an orphaned add) renders
  // without a link entry — so the header menu's "Link" row and the panel's
  // own channel-follow behaviour silently vanish, while Duplicate / Undock
  // (which read the dockview API) keep working. Re-register the record from
  // the dockview-authoritative component so the panel rejoins the default
  // channel. Guarded on a fresh getState() read so we never clobber a
  // just-written entry this render's snapshot hasn't caught up to.
  useEffect(() => {
    if (!tabId || panel) return;
    const component = props.api.component as string | undefined;
    if (!component) return;
    const tab = useAppStore.getState().tabs[tabId];
    if (!tab || tab.panels[panelId]) return;
    addPanelToStore(tabId, { id: panelId, component, title: props.api.title ?? '' });
  }, [tabId, panelId, panel, props.api, addPanelToStore]);

  const title = displayTitle(props.api.title ?? '', props.api.component as string);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      // Defer to next frame so the input is mounted before we focus
      // it; also select the whole title so a single keystroke
      // replaces it (matches OS rename UX).
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing]);

  function startEdit() {
    setDraft(title);
    setEditing(true);
  }

  function commitEdit() {
    const next = draft.trim();
    if (!editing) return;
    setEditing(false);
    if (!next || next === title) return;
    setPanelTitle(tabId, panelId, next);
    props.api.setTitle(next);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft(title);
  }

  return (
    <div className="dv-default-tab hc-panel-tab">
      {/* Drag-grip glyph — universal "grab to move" affordance.
          aria-hidden because the whole tab is already keyboard-grabbable
          via dockview's drag affordance; this is purely visual. */}
      <GripVertical
        size={12}
        aria-hidden
        className="hc-panel-tab__grip"
        style={{
          color: 'var(--text-tertiary)',
          flexShrink: 0,
          marginRight: 2,
          opacity: 0.55,
        }}
      />
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitEdit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
            // Stop other keys (e.g. arrow keys, space) from being
            // hijacked by dockview's keybindings.
            e.stopPropagation();
          }}
          onBlur={commitEdit}
          // Don't let pointer events propagate to dockview — would
          // start a drag on the tab.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="hc-panel-tab__rename-input"
        />
      ) : (
        <span
          className="dv-default-tab-content"
          onDoubleClick={(e) => {
            e.stopPropagation();
            startEdit();
          }}
          title="Double-click to rename"
        >
          {title}
        </span>
      )}
      <PanelHeaderMenu tabId={tabId} panelId={panelId} dockviewPanelApi={props.api} />
    </div>
  );
}

// Right-side group header action — the ✕ close button at the top-right corner
// of each panel (the same place as the cell-detail modal's ✕). Closes the
// group's active panel AND drops its store record, the same pair the header
// menu's Delete performs.
//
// dockview's onDidRemovePanel can't do this for us: TabCanvas is keyed on the
// active tab, so switching tabs unmounts the whole dockview and would fire a
// remove for every panel — syncing there would wipe the layout on every tab
// switch. Closing is therefore reconciled at the two ends: explicitly here,
// and by pruneOrphanPanels when the tab next mounts (shell.tsx).
export function PanelHeaderRightActions(props: IDockviewHeaderActionsProps) {
  const removePanel = useAppStore((s) => s.actions.removePanel);
  return (
    <div className="hc-group-actions">
      <button
        type="button"
        className="hc-panel-tab__close"
        aria-label="Close panel"
        title="Close panel"
        onClick={() => {
          const panel = props.group.activePanel;
          if (!panel) return;
          const params = panel.params as { tabId?: TabId; panelId?: PanelId } | undefined;
          if (params?.tabId && params.panelId) removePanel(params.tabId, params.panelId);
          panel.api.close();
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}
